import { transformUnits } from '@/helpers/unit-formatter/unit-formatter';
import { FormattedUnitLabels, UnitValue } from '@/helpers/unit-formatter/unit-formatter.types';
import { unitValueToFormattedUnitLabels } from '@/helpers/unit-formatter/unit-formatter.utils';
import { isNullOrUndefined } from '@/utils/is-null-or-undefined';

import { SUPPORTED_UNITS } from '../unit-formatter.constants';

/**
 * Formatter for duration values.
 * Formats duration values in H:MM format (hours without leading zero, minutes with leading zero).
 *
 * Normalizes input units to 'sec' when units is undefined, empty, 'none', or 'duration'.
 * This ensures values stored as raw seconds or marked as 'duration' are properly transformed.
 *
 * NOTE: Precision is lost when converting to H:MM format since seconds are not displayed.
 * Converting back from duration to sec/min/hrs will not recover the original seconds component.
 *
 * @param {UnitValue} unitValue - The duration value and units to format
 * @param {string} [displayUnits] - Target display units ('duration' or 'timer')
 * @returns {FormattedUnitLabels} Formatted labels with valueLabel in H:MM format
 *
 * @example
 * formatDuration({ value: 3600, units: 'sec' }, 'duration')
 * // Returns: { valueLabel: '1:00', unitsLabel: '', valueUnitsLabel: '1:00' }
 *
 * @example
 * formatDuration({ value: 3600, units: 'duration' }, 'duration')
 * // Returns: { valueLabel: '1:00', unitsLabel: '', valueUnitsLabel: '1:00' }
 */
export const formatDuration = (
  unitValue: UnitValue,
  displayUnits?: string
): FormattedUnitLabels => {
  // Normalize units to 'sec' for values that are already in seconds:
  // - undefined/empty: no units specified, assume seconds
  // - 'none': explicitly no units, assume seconds
  // - 'duration': display-only format where underlying value is in seconds
  if (
    isNullOrUndefined(unitValue.units) ||
    unitValue.units === '' ||
    unitValue.units === SUPPORTED_UNITS.NONE ||
    unitValue.units?.toLowerCase() === SUPPORTED_UNITS.DURATION ||
    unitValue.units?.toLowerCase() === SUPPORTED_UNITS.TIMER
  ) {
    unitValue.units = SUPPORTED_UNITS.SEC;
  }

  const transformed = transformUnits(unitValue, displayUnits);

  return unitValueToFormattedUnitLabels({
    value: transformed.value,
    units: transformed.units,
  });
};
